import React, { useState, useEffect } from 'react';
import UniversalNavbar from '../components/UniversalNavbar';

// Hooks
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useOrders } from '../hooks/useOrders';
import { useWorkers } from '../hooks/useWorkers';
import { usePayments } from '../hooks/usePayments';
import { useCustomers } from '../hooks/useCustomers';

// Components
import { PaymentsTab } from './admin/components/PaymentsTab';
import { PaymentVerificationModal } from './admin/components/PaymentVerificationModal';
import { OrdersTab } from './admin/components/OrdersTab';
import { OrderDetailsModal } from './admin/components/OrderDetailsModal';
import { WorkersTab } from './admin/components/WorkersTab';
import { WorkerModal, WorkerSelectionModal } from './admin/components/WorkerModals';
import { CustomersTab } from './admin/components/CustomersTab';
import { CustomerDetailsModal } from './admin/components/CustomerDetailsModal';
import { CustomerDashboardViewModal } from './admin/components/CustomerDashboardViewModal';
import { PhotoPackagesTab } from './admin/components/PhotoPackagesTab';
import { PhotoPackagePaymentsTab } from './admin/components/PhotoPackagePaymentsTab';

const AdminPortal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'payments' | 'orders' | 'workers' | 'customers' | 'photo-packages' | 'photo-payments'>('payments');

  // Auth
  const {
    isAuthenticated,
    adminInfo,
    loading: authLoading,
    verifyToken,
    login,
    logout
  } = useAdminAuth();

  // Orders
  const {
    orders,
    selectedOrder,
    showOrderModal,
    isEditing,
    editedOrder,
    loadOrders,
    updateOrderStatus,
    saveOrderChanges,
    viewOrderDetails,
    closeOrderModal,
    startEditing,
    cancelEditing,
    handleEditChange
  } = useOrders();

  // Workers
  const {
    allWorkers,
    availableWorkers,
    selectedWorker,
    showWorkerModal,
    isEditingWorker,
    showWorkerSelection,
    setSelectedWorker,
    setShowWorkerModal,
    setIsEditingWorker,
    setShowWorkerSelection,
    loadAllWorkers,
    loadAvailableWorkers,
    assignWorkerToOrder,
    saveWorker,
    openWorkerSelectionModal
  } = useWorkers();

  // Payments
  const {
    pendingPayments,
    selectedPayment,
    showPaymentModal,
    imageUrls,
    setSelectedPayment,
    setShowPaymentModal,
    loadPendingPayments,
    fetchImageWithAuth,
    verifyPayment,
    rejectPayment
  } = usePayments();

  // Customers
  const {
    customers,
    selectedCustomer,
    showCustomerModal,
    customerOrders,
    loadCustomers,
    loadCustomerDetails,
    viewCustomerDashboard,
    closeCustomerModal
  } = useCustomers();

  // Customer dashboard view state
  const [showDashboardView, setShowDashboardView] = useState(false);
  const [dashboardData, setDashboardData] = useState<any>(null);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      verifyToken(token, () => {
        loadOrders();
        loadPendingPayments();
        loadAvailableWorkers();
        loadAllWorkers();
        loadCustomers();
      });
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    login(loginData.email, loginData.password, () => {
      loadOrders();
      loadPendingPayments();
      loadAvailableWorkers();
      loadAllWorkers();
      loadCustomers();
    });
  };

  const handleUpdateOrderStatus = (orderId: string, newStatus: string) => {
    if (newStatus === 'assigned') {
      openWorkerSelectionModal(orderId);
    } else {
      updateOrderStatus(orderId, newStatus);
    }
  };

  const handleWorkerAssignment = (workerId: string) => {
    assignWorkerToOrder(workerId, () => {
      loadOrders();
    });
  };

  const handleViewWorkerPortal = async (workerId: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/auth/admin/worker-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ workerId })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('workerToken', data.token);
        window.location.href = '/worker-portal';
      } else {
        alert('Failed to access worker view');
      }
    } catch (error) {
      console.error('Worker view error:', error);
      alert('Failed to access worker view');
    }
  };

  const handleDeleteWorker = (worker: any) => {
    if (window.confirm(`Are you sure you want to delete worker ${worker.name}? This action cannot be undone.`)) {
      alert('Delete functionality coming soon');
    }
  };

  const handleViewCustomerDashboard = async (customerId: string) => {
    const data = await viewCustomerDashboard(customerId);
    if (data) {
      setDashboardData(data);
      setShowDashboardView(true);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="portal-container" style={{ marginTop: '100px' }}>
        <div className="login-form" style={{
          maxWidth: '400px',
          margin: '0 auto',
          background: 'white',
          padding: '2rem',
          borderRadius: '15px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem', color: '#333' }}>
            {'Acceso de Administrador'}
          </h2>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                {'Correo'}
              </label>
              <input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                {'Contraseña'}
              </label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={authLoading}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: authLoading ? 'not-allowed' : 'pointer',
                opacity: authLoading ? 0.7 : 1
              }}
            >
              {authLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>

          {/* Portal Links for Workers and Customers */}
          <div style={{
            textAlign: 'center',
            marginTop: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e0e0e0'
          }}>
            <p style={{
              fontSize: '0.85rem',
              color: '#666',
              marginBottom: '0.5rem'
            }}>
              {'Not an admin?'}
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              alignItems: 'center'
            }}>
              <a
                href="/worker-portal"
                style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#228B22'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                {'Worker Login'} →
              </a>
              <span style={{ color: '#ddd' }}>|</span>
              <a
                href="/customer-portal"
                style={{
                  fontSize: '0.85rem',
                  color: '#666',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#228B22'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
              >
                {'Customer Login'} →
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <UniversalNavbar
        userType="admin"
        user={adminInfo || undefined}
        onLogout={logout}
        
      />
      <div className="portal-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px', marginTop: '100px' }}>
        {/* Dashboard Header */}
        <div style={{
          background: 'linear-gradient(135deg, #228B22 0%, #32CD32 100%)',
          color: 'white',
          padding: '2rem',
          borderRadius: '15px',
          marginBottom: '2rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <h1>{'Panel de Administrador'}</h1>
            <p style={{ margin: 0, opacity: 0.9, marginTop: '0.5rem' }}>
              {'Manage tree planting operations and monitor progress'}
            </p>
            {adminInfo && (
              <p style={{ margin: 0, opacity: 0.8, fontSize: '0.9rem', marginTop: '0.5rem' }}>
                Welcome, {adminInfo.email}
              </p>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          borderBottom: '1px solid #eee'
        }}>
          <button
            onClick={() => setActiveTab('payments')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              background: activeTab === 'payments' ? '#228B22' : 'transparent',
              color: activeTab === 'payments' ? 'white' : '#666',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Manual Payment Verification ({pendingPayments.length})
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              background: activeTab === 'orders' ? '#228B22' : 'transparent',
              color: activeTab === 'orders' ? 'white' : '#666',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Tree Planting Orders ({orders.length})
          </button>
          <button
            onClick={() => setActiveTab('workers')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              background: activeTab === 'workers' ? '#228B22' : 'transparent',
              color: activeTab === 'workers' ? 'white' : '#666',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Worker Management ({allWorkers.length})
          </button>
          <button
            onClick={() => setActiveTab('customers')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              background: activeTab === 'customers' ? '#228B22' : 'transparent',
              color: activeTab === 'customers' ? 'white' : '#666',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Customer Management ({customers.length})
          </button>
          <button
            onClick={() => setActiveTab('photo-packages')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              background: activeTab === 'photo-packages' ? '#228B22' : 'transparent',
              color: activeTab === 'photo-packages' ? 'white' : '#666',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            📸 Photo Packages
          </button>
          <button
            onClick={() => setActiveTab('photo-payments')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              background: activeTab === 'photo-payments' ? '#228B22' : 'transparent',
              color: activeTab === 'photo-payments' ? 'white' : '#666',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            📸💳 Photo Payments
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'payments' && (
          <PaymentsTab
            pendingPayments={pendingPayments}
            onReviewPayment={(payment) => {
              setSelectedPayment(payment);
              setShowPaymentModal(true);
            }}
          />
        )}

        {activeTab === 'orders' && (
          <OrdersTab
            orders={orders}
            onViewDetails={viewOrderDetails}
            onUpdateStatus={handleUpdateOrderStatus}
          />
        )}

        {activeTab === 'workers' && (
          <WorkersTab
            workers={allWorkers}
            onCreateWorker={() => {
              setSelectedWorker(null);
              setIsEditingWorker(false);
              setShowWorkerModal(true);
            }}
            onEditWorker={(worker) => {
              setSelectedWorker(worker);
              setIsEditingWorker(true);
              setShowWorkerModal(true);
            }}
            onDeleteWorker={handleDeleteWorker}
            onViewWorkerPortal={handleViewWorkerPortal}
          />
        )}

        {activeTab === 'customers' && (
          <CustomersTab
            customers={customers}
            onViewDetails={(customer) => loadCustomerDetails(customer._id)}
            onViewAsCustomer={handleViewCustomerDashboard}
          />
        )}

        {activeTab === 'photo-packages' && (
          <PhotoPackagesTab
            onAssignWorker={(packageId, scheduleIndex) => {
              // TODO: Implement worker assignment modal for photo packages
              alert('Worker assignment coming soon');
            }}
            onViewDetails={(photoPackage) => {
              // TODO: Implement photo package details modal
              alert('Package details modal coming soon');
            }}
          />
        )}

        {activeTab === 'photo-payments' && (
          <PhotoPackagePaymentsTab
            onVerifyPayment={(paymentId, notes) => {
              console.log('Photo package payment verified:', paymentId, notes);
            }}
            onRejectPayment={(paymentId, reason) => {
              console.log('Photo package payment rejected:', paymentId, reason);
            }}
            onViewEvidence={(payment) => {
              // TODO: Implement evidence viewer modal for photo packages
              alert('Evidence viewer coming soon');
            }}
          />
        )}

        {/* Modals */}
        {showPaymentModal && selectedPayment && (
          <PaymentVerificationModal
            payment={selectedPayment}
            imageUrls={imageUrls}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedPayment(null);
            }}
            onVerify={(orderId) => verifyPayment(orderId, loadOrders)}
            onReject={rejectPayment}
            onFetchImage={fetchImageWithAuth}
          />
        )}

        {showOrderModal && selectedOrder && (
          <OrderDetailsModal
            order={selectedOrder}
            editedOrder={editedOrder}
            isEditing={isEditing}
            onClose={closeOrderModal}
            onStartEditing={startEditing}
            onCancelEditing={cancelEditing}
            onSave={saveOrderChanges}
            onEditChange={handleEditChange}
            onUpdateStatus={handleUpdateOrderStatus}
          />
        )}

        {showWorkerModal && (
          <WorkerModal
            worker={selectedWorker}
            isEditing={isEditingWorker}
            onClose={() => {
              setShowWorkerModal(false);
              setSelectedWorker(null);
              setIsEditingWorker(false);
            }}
            onSave={saveWorker}
          />
        )}

        {showWorkerSelection && (
          <WorkerSelectionModal
            workers={availableWorkers}
            onSelect={handleWorkerAssignment}
            onClose={() => setShowWorkerSelection(false)}
          />
        )}

        {showCustomerModal && selectedCustomer && (
          <CustomerDetailsModal
            customer={selectedCustomer}
            orders={customerOrders}
            onClose={closeCustomerModal}
            onViewOrder={viewOrderDetails}
          />
        )}

        {showDashboardView && dashboardData && (
          <CustomerDashboardViewModal
            dashboardData={dashboardData}
            onClose={() => {
              setShowDashboardView(false);
              setDashboardData(null);
            }}
          />
        )}

        <style>{`
          .status-pending { background: #fff3cd; color: #856404; }
          .status-assigned { background: #d4edda; color: #155724; }
          .status-planted { background: #d1ecf1; color: #0c5460; }
          .status-completed { background: #d1e7dd; color: #0a3622; }
        `}</style>
      </div>
    </>
  );
};

export default AdminPortal;
